import { api } from './api.ts'

export type PushSupport = 'ready' | 'needs-install' | 'unsupported'

export const isIos = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true

/** iPhone only allows push for a site added to the home screen and opened from its icon. */
export const pushSupport = (): PushSupport => {
  if (isIos() && !isStandalone()) return 'needs-install'
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
    ? 'ready'
    : 'unsupported'
}

export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return undefined
  try {
    return await navigator.serviceWorker.register('/sw.js')
  } catch {
    return undefined
  }
}

const keyBytes = (base64Url: string) => {
  const base64 = (base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
}

const sameKey = (current: ArrayBuffer | null, expected: Uint8Array) => {
  if (!current) return false
  const bytes = new Uint8Array(current)
  return bytes.length === expected.length && bytes.every((byte, index) => byte === expected[index])
}

export const deviceSubscription = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null
  const registration = await navigator.serviceWorker.getRegistration()
  return (await registration?.pushManager.getSubscription()) ?? null
}

const save = (subscription: PushSubscription) => {
  const json = subscription.toJSON()
  return api('/inbox/push-subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    }),
  })
}

export const enablePush = async (publicKey: string) => {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted')
    throw new Error(
      permission === 'denied'
        ? 'Сповіщення заблоковано в налаштуваннях браузера. Дозвольте їх для цього сайту.'
        : 'Дозвіл на сповіщення не надано.',
    )
  await registerServiceWorker()
  const registration = await navigator.serviceWorker.ready
  const key = keyBytes(publicKey)
  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
    await subscription.unsubscribe()
    subscription = null
  }
  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  })
  await save(subscription)
}

export const disablePush = async () => {
  const subscription = await deviceSubscription()
  if (!subscription) return
  await api('/inbox/push-subscriptions/remove', {
    method: 'POST',
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  })
  await subscription.unsubscribe()
}

/** Re-sends this device's subscription so the server always routes it to the signed-in person. */
export const refreshPush = async (publicKey: string | null) => {
  if (!publicKey || pushSupport() !== 'ready' || Notification.permission !== 'granted') return
  const subscription = await deviceSubscription()
  if (!subscription) return
  if (sameKey(subscription.options.applicationServerKey, keyBytes(publicKey)))
    await save(subscription)
  else await enablePush(publicKey)
}
